// syntaxFloor.js — minimum executable verifier floor (no-silent-green / M2).
// @arch:pure-engine
//
// AUDIT FINDING (convergent): when a code-changing turn injects NO project verifier (no lint/
// typecheck/test script matched), the dominant path becomes silent green. The literature
// (ImpossibleBench) also warns that an agent-VISIBLE verifier is a gameable target. The
// invariant-preserving fix: synthesize a REAL, cheap, always-available executable judge —
// `node --check` syntax validation on the files the turn actually changed. It is ground-truth
// executable verification (catches broken syntax), NOT an LLM judge and NOT a confidence score,
// so it strengthens the LAW rather than weakening it. For files node can't parse on its own
// (.jsx/.ts), there is no floor — those fall through to the explicit 'unverified' state (M1).
//
// PURE engine (Tier 1): the shell port `runCommand` is INJECTED; no direct I/O.

// node --check parses these as-is; .jsx/.tsx/.ts need a transform → intentionally out of scope.
export const CHECKABLE = /\.(?:js|mjs|cjs)$/i;

function exitOf(r) {
  if (r && typeof r === 'object') return r.exitCode ?? (r.passed === false ? 1 : 0);
  // string result: a leading "exitCode: N" line (run_command format) or plain output
  const m = /^exitCode:\s*(\d+)/.exec(String(r || ''));
  return m ? Number(m[1]) : 0;
}

/**
 * List the changed source files node can syntax-check (repo-relative), via `git diff`.
 * @returns {Promise<string[]>}
 */
export async function listChangedJsFiles({ root, runCommand } = {}) {
  if (typeof runCommand !== 'function') return [];
  try {
    const r = await runCommand({ command: 'git diff --name-only HEAD', cwd: root });
    const out = r && typeof r === 'object' ? (r.stdout || '') : String(r || '');
    return out.split('\n').map((s) => s.trim()).filter((s) => s && CHECKABLE.test(s));
  } catch {
    return [];
  }
}

/**
 * Build a `node --check` verifier (shape: async () => {passed, evidence}) for the given files,
 * or null when nothing is checkable. Plugs straight into runOracle's verifier map — so when it is
 * the only verifier, the oracle produces a REAL verdict instead of noVerifiers→silent-green.
 * @param {object} deps
 * @param {string[]} deps.touchedFiles  repo-relative paths the turn changed
 * @param {string}   deps.root          project root (shell cwd)
 * @param {Function} deps.runCommand    INJECTED shell port ({command,cwd})=>{exitCode,stdout,stderr}
 * @returns {(Function|null)}
 */
export function buildSyntaxFloorVerifier({ touchedFiles, root, runCommand } = {}) {
  const files = (Array.isArray(touchedFiles) ? touchedFiles : [])
    .filter((f) => typeof f === 'string' && CHECKABLE.test(f));
  if (files.length === 0 || typeof runCommand !== 'function') return null;
  return async () => {
    for (const f of files) {
      const r = await runCommand({ command: `node --check ${JSON.stringify(f)}`, cwd: root });
      if (exitOf(r) !== 0) {
        const why = (r && typeof r === 'object' && (r.stderr || r.evidence)) || `node --check failed: ${f}`;
        return { passed: false, evidence: `syntax: ${String(why).trim().slice(0, 200)}` };
      }
    }
    return { passed: true, evidence: `syntax ok (${files.length} file${files.length > 1 ? 's' : ''})` };
  };
}

export default { buildSyntaxFloorVerifier, listChangedJsFiles, CHECKABLE };
